import { useCallback, useEffect, useState } from 'react'
import { MEMORY_ENABLED, MemoryError, fetchMemory, type RecordMemory } from './memoryApi'

/** The record's Customer Memory, loaded once as the record opens and again whenever it changes. */
export function useRecordMemory(entityType: 'lead' | 'enquiry', entityId: string, enabled: boolean) {
  const on = MEMORY_ENABLED && enabled && !!entityId
  const [memory, setMemory] = useState<RecordMemory | null>(null)
  const [loading, setLoading] = useState(on)
  const [error, setError] = useState<string | null>(null)
  const [signedOut, setSignedOut] = useState(false)
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    if (!on) {
      setLoading(false)
      return
    }
    const controller = new AbortController()
    setLoading(true)
    fetchMemory(entityType, entityId, controller.signal)
      .then((m) => {
        setMemory(m)
        setError(null)
        setSignedOut(false)
      })
      .catch((err: Error) => {
        if (err.name === 'AbortError') return
        // Memory failing to load must never take the panel down with it; it is reported in its own section.
        setError(err instanceof MemoryError ? err.message : 'It could not be read.')
        setSignedOut(err instanceof MemoryError && err.signedOut)
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [on, entityType, entityId, nonce])

  const reload = useCallback(() => setNonce((n) => n + 1), [])
  return { offered: on, memory, loading, error, signedOut, reload }
}
